/**
 * Syllabus import — turns a spreadsheet, a pasted grid or a pasted outline into
 * the editor's `days[]` / `modules[]`.
 *
 * Pure: no React, no network, no SheetJS. The dialog reads the file, hands the
 * rows here, shows what comes back, and the editor loads it. Nothing is saved
 * until the admin presses Save — the same contract as the AI draft.
 *
 * Two readers, one builder. `readGrid` (Excel / CSV / cells pasted from a
 * spreadsheet) and `readOutline` (indented text pasted from a document) both
 * reduce their input to one stream of entries — a module or day, a topic, a
 * subtopic, a duration — each carrying the row or line it came from.
 * `buildSyllabus` is the only place that merges, numbers, limits and reports,
 * so both inputs are held to exactly one set of rules.
 *
 * Reporting rule: an ERROR means the result would be wrong or unsaveable and
 * blocks loading; a WARNING means we made a reasonable choice the admin should
 * know about (merged, renumbered, defaulted) and does not.
 */

export const LIMITS = {
  rows: 5000,
  modules: 200,
  days: 365,
  topicsPerGroup: 200,
  subtopicsPerTopic: 200,
  title: 200,
  durationMinutes: 24 * 60,
};

/** What the editor puts in an empty duration field — keep the two in step. */
export const DEFAULT_DURATION = 60;

const clean = (v) => String(v ?? "").replace(/ /g, " ").replace(/\s+/g, " ").trim();
const keyOf = (s) => clean(s).toLowerCase();

/** "Module No." → "moduleno", "Module #" → "moduleno", "Sub-topics" → "subtopics". */
const normHeader = (s) => String(s ?? "").toLowerCase().replace(/#/g, "no").replace(/[^a-z0-9]/g, "");

const COLUMN_ALIASES = {
  moduleNo: ["moduleno", "modulenumber", "modno", "unitno", "unitnumber", "chapterno", "chapternumber"],
  module: ["module", "modules", "moduletitle", "modulename", "unit", "unittitle", "unitname", "chapter", "chaptertitle", "chaptername"],
  day: ["day", "dayno", "daynumber", "session", "sessionno", "sessionnumber"],
  dayTitle: ["daytitle", "dayname", "sessiontitle", "sessionname", "title"],
  topic: ["topic", "topics", "topictitle", "topicname", "lesson", "lessons", "lessontitle"],
  subtopics: ["subtopic", "subtopics", "subtopictitle", "subtopicname"],
  duration: [
    "duration", "durationmins", "durationmin", "durationminutes", "durationinminutes",
    "mins", "minutes", "min", "estimatedduration", "estimatedtime", "time", "timemins",
    "durationhrs", "durationhours", "durationinhours", "hours", "hrs",
  ],
};

/** Header text for each column, in the order the templates write them. */
export const TEMPLATE_HEADERS = {
  module: ["Module No", "Module", "Topic", "Duration (mins)", "Subtopics"],
  day: ["Day", "Day Title", "Topic", "Duration (mins)"],
};

// ── Small parsers ───────────────────────────────────────────────────────────

/** Leading list markers a document or a cell carries: "-", "•", "1.", "1.2", "a)", "iv.". */
const LIST_MARKER = /^(?:[-*•◦▪·–]|\d+(?:\.\d+)+\.?|\d+[.)]|[a-z][.)]|[ivx]+[.)])\s+/i;

const stripMarker = (s) => clean(clean(s).replace(LIST_MARKER, ""));

/**
 * One cell holding several items. Newline (Alt+Enter in Excel), "|", ";" and
 * "•" separate; a comma does NOT, because commas live inside real topic names
 * ("Variables, data types and operators").
 */
export function splitItems(cell) {
  return String(cell ?? "")
    .split(/\r?\n|\||;|•/)
    .map(stripMarker)
    .filter(Boolean);
}

/** "3", "3.0", "Module 3", "Day-12" → the integer; "3.5", "1st", "abc" → null. */
export function parseNumber(raw) {
  const s = clean(raw);
  if (!s) return null;
  const m = s.match(/^(?:[a-z]+\s*[-#:.]?\s*)?(\d+)(?:\.0+)?$/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * A duration in minutes. Accepts "90", "1.5h", "1 hr 30 min", "45m", "01:30"
 * (Excel shows a time-formatted cell that way). `inHours` reads a bare number
 * as hours — set when the column header says "hours".
 * Returns null for empty, `NaN` for something that is not a duration.
 */
export function parseDuration(raw, inHours = false) {
  const s = clean(raw).toLowerCase();
  if (!s) return null;
  let m = s.match(/^(\d{1,3}):(\d{1,2})(?::\d{1,2})?$/);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = s.match(/^(?:(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours))?\s*(?:(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes))?$/);
  if (m && (m[1] || m[2])) return Math.round(Number(m[1] || 0) * 60 + Number(m[2] || 0));
  if (/^\d+(?:\.\d+)?$/.test(s)) return Math.round(Number(s) * (inHours ? 60 : 1));
  return NaN;
}

// ── Grid reader (Excel / CSV / pasted cells) ────────────────────────────────

function mapHeaderRow(cells) {
  const map = {};
  (cells || []).forEach((cell, i) => {
    const n = normHeader(cell);
    if (!n) return;
    for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
      if (map[field] === undefined && aliases.includes(n)) {
        map[field] = i;
        if (field === "duration") map.durationInHours = /hour|hrs/.test(n);
        if (field === "dayTitle") map.dayTitleIsBare = n === "title";
        break;
      }
    }
  });
  return map;
}

/**
 * Find the header row (allowing a title row or two above it) and decide the
 * format from it: a Module column means Module → Topic → Subtopic, a Day
 * column means day-wise. A Topic column is required either way.
 */
function detectLayout(rows) {
  const scan = Math.min(rows.length, 15);
  for (let r = 0; r < scan; r++) {
    const map = mapHeaderRow(rows[r]);
    if (map.topic === undefined) continue;
    const hasModule = map.module !== undefined || map.moduleNo !== undefined;
    const hasDay = map.day !== undefined || (map.dayTitle !== undefined && !map.dayTitleIsBare);
    if (hasModule) {
      // "Module No | Title | Topic" — a bare Title column names the module.
      if (map.module === undefined && map.dayTitle !== undefined && map.dayTitleIsBare) map.module = map.dayTitle;
      return { headerIndex: r, format: "module", map, ignoredDayColumn: map.day !== undefined };
    }
    if (hasDay) return { headerIndex: r, format: "day", map };
  }
  return null;
}

/**
 * Read a grid of cells into import entries.
 *
 * @param {Array<Array<string>>} rows  cells as text, header row included
 * @param {object} [opts]
 * @param {number} [opts.firstRowNumber=1]  spreadsheet row number of rows[0],
 *   so every message names the row the admin sees in Excel
 * @returns {{ format, entries, warnings } | { error }}
 */
export function readGrid(rows, { firstRowNumber = 1 } = {}) {
  const data = Array.isArray(rows) ? rows : [];
  if (!data.some((r) => (r || []).some((c) => clean(c)))) {
    return { error: "The file is empty." };
  }
  const layout = detectLayout(data);
  if (!layout) {
    return {
      error:
        "Couldn't find the header row. The first row should name the columns — " +
        `"${TEMPLATE_HEADERS.module.join('", "')}" for a module syllabus, or ` +
        `"${TEMPLATE_HEADERS.day.join('", "')}" for a day-wise one. Download a template to start from.`,
    };
  }
  const body = data.slice(layout.headerIndex + 1);
  if (body.length > LIMITS.rows) {
    return { error: `The file has ${body.length.toLocaleString("en-IN")} rows — the most one import takes is ${LIMITS.rows.toLocaleString("en-IN")}.` };
  }

  const { map, format } = layout;
  const cell = (row, idx) => (idx === undefined ? "" : clean((row || [])[idx]));
  const entries = [];
  const warnings = [];
  if (layout.ignoredDayColumn) {
    warnings.push({ row: null, message: "The file has both a Module and a Day column — it was read as a module syllabus and the Day column was ignored." });
  }

  body.forEach((r, i) => {
    const row = firstRowNumber + layout.headerIndex + 1 + i;
    if (format === "module") {
      const number = cell(r, map.moduleNo);
      const title = cell(r, map.module);
      const topic = cell(r, map.topic);
      const subs = (r || [])[map.subtopics];
      const duration = cell(r, map.duration);
      if (number || title) entries.push({ kind: "group", rawNumber: number, title, row });
      if (topic) entries.push({ kind: "topic", title: stripMarker(topic), row });
      if (duration) entries.push({ kind: "duration", raw: duration, inHours: !!map.durationInHours, row });
      for (const s of splitItems(subs)) entries.push({ kind: "subtopic", title: s, row });
    } else {
      const number = cell(r, map.day);
      const title = cell(r, map.dayTitle);
      const topics = (r || [])[map.topic];
      const duration = cell(r, map.duration);
      if (number || title) entries.push({ kind: "group", rawNumber: number, title, row });
      if (duration) entries.push({ kind: "duration", raw: duration, inHours: !!map.durationInHours, row });
      for (const t of splitItems(topics)) entries.push({ kind: "topic", title: t, row });
    }
  });

  return { format, entries, warnings };
}

/**
 * Parse delimited text — a pasted spreadsheet selection (tabs) or a CSV —
 * honouring quotes, so a cell holding a line break (Alt+Enter) survives.
 */
export function parseDelimited(text, delimiter = "\t") {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  const s = String(text ?? "").replace(/\r\n?/g, "\n");
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += ch;
    } else if (ch === '"' && field === "") {
      quoted = true;
    } else if (ch === delimiter) {
      row.push(field); field = "";
    } else if (ch === "\n") {
      row.push(field); rows.push(row); row = []; field = "";
    } else field += ch;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// ── Outline reader (text pasted from a document) ────────────────────────────

/**
 * "Module 1: Core Java", "Unit 2 – OOP", "Day 3 - Loops", "Module: Core Java".
 * The keyword needs a number or a colon after it, so a topic that merely starts
 * with the word ("Session management in servlets") stays a topic.
 */
const GROUP_LINE = /^(module|unit|chapter|section|part|day|session|week)\s*(?:#?\s*(\d+)\b\s*[-–:.)]?\s*(.*)|[-–:]\s*(.+))$/i;
const DAY_WORDS = new Set(["day", "session"]);
/** "(90 min)", "[1.5h]", "(1 hr 30 min)", "(01:30)" — a unit is required, so "(Part 2)" and "(2)" are titles. */
const TRAILING_DURATION = /\s*[([]\s*(\d+(?:\.\d+)?\s*(?:h|hrs?|hours?|m|mins?|minutes?)\b(?:\s*\d+\s*(?:m|mins?|minutes?)\b)?|\d{1,2}:\d{2})\s*[)\]]\s*$/i;

function markerClass(text) {
  if (/^\d+\.\d+/.test(text)) return "dotted";
  if (/^\d+[.)]\s/.test(text)) return "num";
  if (/^[a-z][.)]\s/i.test(text) || /^[ivx]+[.)]\s/i.test(text)) return "alpha";
  if (/^[-*•◦▪·–]\s/.test(text)) return "bullet";
  return "none";
}

/** Pull "(90 min)" / "[1.5h]" off the end of a line. */
function takeDuration(text) {
  const m = text.match(TRAILING_DURATION);
  if (!m) return { title: text, raw: "" };
  return { title: clean(text.slice(0, m.index)), raw: m[1] };
}

/**
 * Read an indented outline into import entries.
 *
 *   Module 1: Core Java            ← "Module/Unit/Chapter N" or "# Heading"
 *     1. Introduction (60 min)     ← a topic; a trailing (duration) is read
 *        - JDK, JRE and JVM        ← deeper, "1.1", "a." → a subtopic
 *
 * "Day N" headings make it a day-wise syllabus, where every line under a day is
 * a topic. With no headings at all, the shallowest indent is the module.
 */
export function readOutline(text) {
  const lines = String(text ?? "").replace(/\r\n?/g, "\n").split("\n")
    .map((raw, i) => {
      const expanded = raw.replace(/\t/g, "    ");
      return { indent: expanded.length - expanded.trimStart().length, text: clean(expanded), line: i + 1 };
    })
    .filter((l) => l.text);
  if (lines.length === 0) return { error: "Nothing to import — paste your syllabus first." };
  if (lines.length > LIMITS.rows) return { error: `That's ${lines.length.toLocaleString("en-IN")} lines — the most one import takes is ${LIMITS.rows.toLocaleString("en-IN")}.` };

  const heading = (l) => {
    const md = l.text.match(/^(#{1,3})\s+(.*)$/);
    if (md) return { level: md[1].length, title: md[2] };
    const g = l.text.match(GROUP_LINE);
    if (g) return { level: 1, word: g[1].toLowerCase(), number: g[2] || "", title: clean(g[3] ?? g[4]) };
    return null;
  };

  const headings = lines.map(heading);
  const topLevel = headings.filter((h) => h && h.level === 1);
  const format = topLevel.length > 0 && topLevel.every((h) => h.word && DAY_WORDS.has(h.word)) ? "day" : "module";
  const entries = [];

  if (topLevel.length === 0) {
    // No "Module N" lines: indentation alone carries the levels.
    const levels = [...new Set(lines.map((l) => l.indent))].sort((a, b) => a - b);
    if (levels.length < 2) {
      return { error: 'Couldn\'t tell modules from topics. Start each module with "Module 1: …" (or "Day 1: …"), or indent topics under their module.' };
    }
    for (const l of lines) {
      const depth = levels.indexOf(l.indent);
      const text = stripMarker(l.text);
      if (depth === 0) entries.push({ kind: "group", rawNumber: "", title: text, row: l.line });
      else if (depth === 1 || format === "day") {
        const { title, raw } = takeDuration(text);
        entries.push({ kind: "topic", title, row: l.line });
        if (raw) entries.push({ kind: "duration", raw, row: l.line });
      } else entries.push({ kind: "subtopic", title: text, row: l.line });
    }
    return { format, entries, warnings: [] };
  }

  // A course title or an intro paragraph above the first module is common in a
  // pasted document; it is not a topic of nothing, so it is skipped and named.
  const firstGroup = headings.findIndex((h) => h && h.level === 1);
  const warnings = firstGroup > 0
    ? [{ row: lines[0].line, message: `${firstGroup === 1 ? "The line" : `${firstGroup} lines`} above the first ${format === "day" ? "day" : "module"} ${firstGroup === 1 ? "was" : "were"} skipped.` }]
    : [];

  let topic = null; // { indent, cls } of the first topic line under the current group
  lines.forEach((l, i) => {
    if (i < firstGroup) return;
    const h = headings[i];
    if (h && h.level === 1) {
      const { title, raw } = takeDuration(h.title);
      entries.push({ kind: "group", rawNumber: h.number || "", title, row: l.line });
      // A day has a duration; a module does not (its topics do).
      if (raw && format === "day") entries.push({ kind: "duration", raw, row: l.line });
      topic = null;
      return;
    }
    if (h && h.level === 2) {
      const { title, raw } = takeDuration(h.title);
      entries.push({ kind: "topic", title, row: l.line });
      if (raw) entries.push({ kind: "duration", raw, row: l.line });
      topic = { indent: l.indent, cls: "heading" };
      return;
    }
    if (h && h.level === 3) {
      entries.push({ kind: format === "day" ? "topic" : "subtopic", title: h.title, row: l.line });
      return;
    }
    const cls = markerClass(l.text);
    const text = stripMarker(l.text);
    const isSub = format === "module" && topic && (
      l.indent > topic.indent
      || cls === "dotted"
      || (l.indent === topic.indent && cls !== topic.cls && ["alpha", "bullet"].includes(cls) && ["num", "none", "heading"].includes(topic.cls))
    );
    if (isSub) {
      entries.push({ kind: "subtopic", title: text, row: l.line });
      return;
    }
    const { title, raw } = takeDuration(text);
    entries.push({ kind: "topic", title, row: l.line });
    if (raw) entries.push({ kind: format === "day" ? "groupDuration" : "duration", raw, row: l.line });
    if (!topic) topic = { indent: l.indent, cls };
  });

  return { format, entries, warnings };
}

// ── The builder ─────────────────────────────────────────────────────────────

/**
 * Turn import entries into the editor's shape, with every problem named.
 *
 * Identity rules, chosen so the common spreadsheet habits all work:
 *  - A module/day with a NUMBER is that number wherever it appears — repeating
 *    "1 | Core Java" on every row (or leaving the cells blank under it) is the
 *    same module, and Module 1 coming back after Module 2 is merged, with a note.
 *  - A module with only a TITLE is matched by title. A day with only a title is
 *    a new day unless it repeats the day just above it, because "Revision" on
 *    Day 10 and Day 20 are two different days.
 *  - A topic is matched by title within its module, so one-subtopic-per-row
 *    sheets that repeat the topic on each row build one topic.
 *  - Numbers are made sequential (1…N) in the order given, which is what the
 *    editor does on every delete; anything renumbered is reported.
 *
 * @param {Array} entries  from readGrid / readOutline
 * @param {object} opts
 * @param {'module'|'day'} opts.format
 * @returns {{ format, modules?, days?, errors, warnings, stats }}
 */
export function buildSyllabus(entries, { format }) {
  const isDay = format === "day";
  const word = isDay ? "Day" : "Module";
  const errors = [];
  const warnings = [];
  const groups = [];
  const byNumber = new Map();
  const byTitle = new Map();
  let current = null;
  let currentTopic = null;
  const truncated = { count: 0 };

  const trimTitle = (s) => {
    const t = clean(s);
    if (t.length <= LIMITS.title) return t;
    truncated.count += 1;
    return t.slice(0, LIMITS.title).trim();
  };

  const startGroup = (number, title, row) => {
    const g = { number, title, row, topics: [], topicByKey: new Map(), duration: null, durationRow: null, order: groups.length };
    groups.push(g);
    if (number != null) byNumber.set(number, g);
    if (title && !isDay) byTitle.set(keyOf(title), g);
    return g;
  };

  const applyDuration = (target, raw, inHours, row) => {
    const minutes = parseDuration(raw, inHours);
    if (minutes == null) return;
    if (Number.isNaN(minutes)) {
      warnings.push({ row, message: `"${clean(raw)}" isn't a duration — ${DEFAULT_DURATION} min was used.` });
      return;
    }
    if (minutes <= 0) return;
    if (minutes > LIMITS.durationMinutes) {
      warnings.push({ row, message: `${minutes} min is longer than a day — ${DEFAULT_DURATION} min was used.` });
      return;
    }
    if (target.duration == null) {
      target.duration = minutes;
      target.durationRow = row;
    } else if (target.duration !== minutes) {
      warnings.push({ row, message: `"${target.title}" has two durations (${target.duration} and ${minutes} min) — kept ${target.duration} min from the first.` });
    }
  };

  for (const e of entries || []) {
    if (e.kind === "group") {
      const number = parseNumber(e.rawNumber);
      if (clean(e.rawNumber) && number == null) {
        warnings.push({ row: e.row, message: `"${clean(e.rawNumber)}" isn't a ${word.toLowerCase()} number — ignored.` });
      }
      const title = trimTitle(e.title);
      let g = null;
      if (number != null && byNumber.has(number)) {
        g = byNumber.get(number);
        if (title && g.title && keyOf(title) !== keyOf(g.title)) {
          errors.push({ row: e.row, message: `${word} ${number} is "${g.title}" on row ${g.row} but "${title}" here. Give each ${word.toLowerCase()} one name, or a different number.` });
        } else if (title && !g.title) {
          g.title = title;
        }
      } else if (number == null && title) {
        if (isDay) {
          if (current && current.number == null && keyOf(current.title) === keyOf(title)) g = current;
        } else {
          g = byTitle.get(keyOf(title)) || null;
        }
      }
      if (g && g !== current && groups[groups.length - 1] !== g) {
        warnings.push({ row: e.row, message: `${word} "${g.title || g.number}" continues here after other ${word.toLowerCase()}s — its rows were merged.` });
      }
      if (!g) g = startGroup(number, title, e.row);
      if (g !== current) currentTopic = null;
      current = g;
      continue;
    }

    if (e.kind === "topic") {
      const title = trimTitle(e.title);
      if (!title) continue;
      if (!current) {
        errors.push({ row: e.row, message: `Topic "${title}" comes before any ${word.toLowerCase()} — put a ${word.toLowerCase()} above it.` });
        continue;
      }
      const k = keyOf(title);
      if (isDay) {
        if (!current.topicByKey.has(k)) {
          current.topicByKey.set(k, title);
          current.topics.push(title);
        }
        continue;
      }
      let t = current.topicByKey.get(k);
      if (t) {
        if (current.topics[current.topics.length - 1] !== t) {
          warnings.push({ row: e.row, message: `Topic "${title}" appears again in ${word} "${current.title || current.number}" — merged with the first.` });
        }
      } else {
        t = { title, row: e.row, duration: null, subtopics: [], subKeys: new Set() };
        current.topicByKey.set(k, t);
        current.topics.push(t);
      }
      currentTopic = t;
      continue;
    }

    if (e.kind === "subtopic") {
      const title = trimTitle(e.title);
      if (!title) continue;
      if (!currentTopic) {
        errors.push({ row: e.row, message: `Subtopic "${title}" has no topic above it.` });
        continue;
      }
      const k = keyOf(title);
      if (!currentTopic.subKeys.has(k)) {
        currentTopic.subKeys.add(k);
        currentTopic.subtopics.push(title);
      }
      continue;
    }

    if (e.kind === "duration" || e.kind === "groupDuration") {
      const target = isDay || e.kind === "groupDuration" ? current : currentTopic;
      if (!target) {
        warnings.push({ row: e.row, message: `The duration "${clean(e.raw)}" has no ${isDay ? "day" : "topic"} to belong to — ignored.` });
        continue;
      }
      applyDuration(target, e.raw, e.inHours, e.row);
    }
  }

  // ── Numbering ──
  if (groups.length === 0) {
    errors.push({ row: null, message: `No ${word.toLowerCase()}s found.` });
  }
  const allNumbered = groups.length > 0 && groups.every((g) => g.number != null);
  if (!allNumbered && groups.some((g) => g.number != null)) {
    warnings.push({ row: null, message: `Some ${word.toLowerCase()}s have a number and some don't — all were numbered in the order they appear.` });
  }
  const ordered = allNumbered ? [...groups].sort((a, b) => a.number - b.number) : groups;
  const given = ordered.map((g) => g.number);
  if (allNumbered && given.some((n, i) => n !== i + 1)) {
    warnings.push({ row: null, message: `${word} numbers were made sequential (1–${ordered.length}); the file had ${summariseNumbers(given)}.` });
  }

  // ── Per-group checks + output ──
  let untitled = 0;
  let defaulted = 0;
  const out = ordered.map((g, i) => {
    const n = i + 1;
    let title = g.title;
    if (!title) {
      untitled += 1;
      title = `${word} ${n}`;
    }
    if (g.topics.length === 0) {
      errors.push({ row: g.row, message: `${word} ${n} "${title}" has no topics.` });
    }
    if (g.topics.length > LIMITS.topicsPerGroup) {
      errors.push({ row: g.row, message: `${word} ${n} has ${g.topics.length} topics — the most one ${word.toLowerCase()} can hold is ${LIMITS.topicsPerGroup}.` });
    }
    if (isDay) {
      if (g.duration == null) defaulted += 1;
      return { dayNumber: n, title, topics: g.topics, estimatedDuration: g.duration ?? DEFAULT_DURATION };
    }
    return {
      moduleNumber: n,
      title,
      topics: g.topics.map((t) => {
        if (t.subtopics.length > LIMITS.subtopicsPerTopic) {
          errors.push({ row: t.row, message: `Topic "${t.title}" has ${t.subtopics.length} subtopics — the most one topic can hold is ${LIMITS.subtopicsPerTopic}.` });
        }
        if (t.duration == null) defaulted += 1;
        return { title: t.title, estimatedDuration: t.duration ?? DEFAULT_DURATION, subtopics: t.subtopics };
      }),
    };
  });

  const max = isDay ? LIMITS.days : LIMITS.modules;
  if (out.length > max) {
    errors.push({ row: null, message: `That's ${out.length} ${word.toLowerCase()}s — the most a syllabus can hold is ${max}.` });
  }
  if (untitled) warnings.push({ row: null, message: `${untitled} ${word.toLowerCase()}${untitled === 1 ? " has" : "s have"} no title and ${untitled === 1 ? "was" : "were"} named "${word} N".` });
  if (truncated.count) warnings.push({ row: null, message: `${truncated.count} title${truncated.count === 1 ? " was" : "s were"} longer than ${LIMITS.title} characters and ${truncated.count === 1 ? "was" : "were"} shortened.` });
  if (defaulted && out.length) {
    const what = isDay ? "day" : "topic";
    warnings.push({ row: null, message: `${defaulted} ${what}${defaulted === 1 ? " has" : "s have"} no duration and will use ${DEFAULT_DURATION} min.` });
  }

  const stats = isDay
    ? {
        days: out.length,
        topics: out.reduce((s, d) => s + d.topics.length, 0),
        minutes: out.reduce((s, d) => s + d.estimatedDuration, 0),
      }
    : {
        modules: out.length,
        topics: out.reduce((s, m) => s + m.topics.length, 0),
        subtopics: out.reduce((s, m) => s + m.topics.reduce((x, t) => x + t.subtopics.length, 0), 0),
        minutes: out.reduce((s, m) => s + m.topics.reduce((x, t) => x + t.estimatedDuration, 0), 0),
      };

  errors.sort(byRow);
  warnings.sort(byRow);
  return isDay
    ? { format, days: out, errors, warnings, stats }
    : { format, modules: out, errors, warnings, stats };
}

function byRow(a, b) {
  if (a.row == null) return b.row == null ? 0 : -1;
  if (b.row == null) return 1;
  return a.row - b.row;
}

function summariseNumbers(nums) {
  const shown = nums.slice(0, 8).join(", ");
  return nums.length > 8 ? `${shown}, …` : shown;
}

/**
 * Read a grid or an outline and build it — the one call the dialog makes.
 * A pasted block with tabs on most lines is a spreadsheet selection; anything
 * else is an outline.
 */
export function importFromGrid(rows, opts) {
  const read = readGrid(rows, opts);
  if (read.error) return { error: read.error };
  const built = buildSyllabus(read.entries, { format: read.format });
  return { ...built, warnings: [...read.warnings, ...built.warnings], unit: "row" };
}

export function importFromText(text) {
  const lines = String(text ?? "").split(/\r?\n/).filter((l) => l.trim());
  const tabbed = lines.filter((l) => l.includes("\t")).length;
  if (lines.length > 0 && tabbed >= Math.ceil(lines.length / 2)) {
    return importFromGrid(parseDelimited(text, "\t"));
  }
  const read = readOutline(text);
  if (read.error) return { error: read.error };
  const built = buildSyllabus(read.entries, { format: read.format });
  return { ...built, warnings: [...read.warnings, ...built.warnings], unit: "line" };
}

// ── Applying to the editor ──────────────────────────────────────────────────

/**
 * What the editor should hold after the import. `append` numbers the imported
 * groups after the existing ones, so nothing already linked by number moves.
 */
export function mergeImport(existing, imported, mode, format) {
  const numberKey = format === "day" ? "dayNumber" : "moduleNumber";
  if (mode !== "append" || !existing?.length) return imported;
  const start = Math.max(...existing.map((g) => Number(g[numberKey]) || 0));
  return [...existing, ...imported.map((g, i) => ({ ...g, [numberKey]: start + i + 1 }))];
}

/**
 * Which existing links a REPLACE would re-point. Materials link to a day or a
 * module by NUMBER plus a topic index, and Smart Sessions to a module number —
 * so after a replace, "Module 3, topic 2" means whatever the file put there.
 * Anything whose title at that position changes (or that no longer exists) is
 * reported, so the admin decides with the list in front of them.
 *
 * @param {object} args
 * @param {Array} args.current         the editor's groups before the import (same format as `links`)
 * @param {Array} args.next            the groups after it (null when the format changes)
 * @param {'module'|'day'} args.format the format `current` and `links` are in
 * @param {object} args.links          from GET /syllabus/template/:courseId/links
 * @returns {Array<{ number, title, nextTitle, materials, smartSessions, removed }>}
 */
export function linkImpact({ current, next, format, links }) {
  const isDay = format === "day";
  const numberKey = isDay ? "dayNumber" : "moduleNumber";
  const source = (isDay ? links?.days : links?.modules) || [];
  const sessions = isDay ? [] : links?.smartSessions || [];
  const byNumber = (list) => new Map((list || []).map((g) => [Number(g[numberKey]), g]));
  const before = byNumber(current);
  const after = byNumber(next);
  const topicTitle = (g, idx) => {
    const t = g?.topics?.[idx];
    return typeof t === "string" ? t : t?.title;
  };

  const impact = new Map();
  const touch = (number) => {
    if (!impact.has(number)) {
      const was = before.get(number);
      const now = after.get(number);
      impact.set(number, {
        number,
        title: was?.title || `${isDay ? "Day" : "Module"} ${number}`,
        nextTitle: now?.title || null,
        removed: !now,
        materials: 0,
        smartSessions: 0,
      });
    }
    return impact.get(number);
  };

  for (const l of source) {
    const number = Number(l.number);
    const was = before.get(number);
    const now = after.get(number);
    let moved = !now || keyOf(was?.title) !== keyOf(now?.title);
    if (!moved && l.topicIndex != null) {
      moved = keyOf(topicTitle(was, l.topicIndex)) !== keyOf(topicTitle(now, l.topicIndex));
    }
    if (moved) touch(number).materials += Number(l.count) || 0;
  }
  for (const s of sessions) {
    const number = Number(s.number);
    const was = before.get(number);
    const now = after.get(number);
    if (!now || keyOf(was?.title) !== keyOf(now?.title)) touch(number).smartSessions += Number(s.count) || 0;
  }
  return [...impact.values()].sort((a, b) => a.number - b.number);
}

// ── Templates ───────────────────────────────────────────────────────────────

/**
 * The sample rows every template carries. One source for the .xlsx, the .csv
 * and the test that proves both import with zero errors — a template that
 * fails its own importer is the worst possible first impression.
 */
export const TEMPLATE_ROWS = {
  module: [
    [1, "Core Java Fundamentals", "Introduction to Java", 60, "History of Java; JDK, JRE and JVM; Writing your first program"],
    ["", "", "Variables and Data Types", 90, "Primitive types; Type casting; Operators and expressions"],
    ["", "", "Control Flow", 90, "if / else; switch; for, while and do-while loops"],
    [2, "Object-Oriented Programming", "Classes and Objects", 90, "Constructors; The this keyword; Static members"],
    ["", "", "Inheritance", 60, "Method overriding"],
    ["", "", "", "", "The super keyword"],
    ["", "", "", "", "Abstract classes"],
    ["", "", "Interfaces and Polymorphism", "1h 30m", "Default methods; Functional interfaces"],
    [3, "Collections Framework", "Lists and Sets", 60, "ArrayList vs LinkedList; HashSet and TreeSet"],
    ["", "", "Maps", 45, "HashMap internals; Iterating a map"],
  ],
  day: [
    [1, "Java Basics & Setup", "Installing the JDK", 90],
    ["", "", "Hello World", ""],
    ["", "", "Variables and data types", ""],
    [2, "Control Flow", "if / else; switch", 90],
    ["", "", "Loops", ""],
    [3, "Object-Oriented Programming", "Classes and objects; Constructors; Inheritance", 120],
    [4, "Revision & Quiz", "Recap of days 1–3; Practice quiz", 60],
  ],
};

export const TEMPLATE_INSTRUCTIONS = {
  module: [
    ["How to fill the Module syllabus template"],
    [""],
    ["Column", "What goes in it"],
    ["Module No", "1, 2, 3 … Optional — without it, modules keep the order they appear in. A number repeated on every row is fine."],
    ["Module", "The module's title. Write it once on its first row; rows below with an empty Module cell belong to the same module."],
    ["Topic", "One topic per row. Repeat the topic name on several rows (or leave it empty) to add more subtopics to it."],
    ["Duration (mins)", "Minutes for the topic: 90, 1.5h, 1h 30m and 01:30 all work. Empty means 60."],
    ["Subtopics", "Optional. Several in one cell separated by ; or | or a line break (Alt+Enter) — or one per row. Commas are kept as part of the text."],
    [""],
    ["Good to know"],
    ["• The first row must be the column headings. Extra columns are ignored, and the columns can be in any order."],
    ["• Merged cells are fine — an empty cell under a module or topic continues it."],
    ["• Nothing is saved when you import. The syllabus loads into the editor, and you review it and press Save & Activate."],
    ["• Importing a module whose number already has materials or Smart Sessions linked will show you what changes before you load it."],
  ],
  day: [
    ["How to fill the Day-wise syllabus template"],
    [""],
    ["Column", "What goes in it"],
    ["Day", "1, 2, 3 … The day of the batch this is taught on. Write it on the day's first row; empty rows below continue the same day."],
    ["Day Title", "The day's theme. Optional — a day without one is named \"Day N\"."],
    ["Topic", "One topic per row, or several in one cell separated by ; or | or a line break (Alt+Enter)."],
    ["Duration (mins)", "Minutes for the whole day, on its first row: 90, 1.5h, 1h 30m and 01:30 all work. Empty means 60."],
    [""],
    ["Good to know"],
    ["• The first row must be the column headings. Extra columns are ignored, and the columns can be in any order."],
    ["• Day numbers are made 1, 2, 3 … in order — a gap in the file is closed up and you are told."],
    ["• Batches that have already started keep the day plan they started with."],
    ["• Nothing is saved when you import. The syllabus loads into the editor, and you review it and press Save & Activate."],
  ],
};

/** The template as plain rows: headings + samples (for CSV and for tests). */
export function templateGrid(format) {
  return [TEMPLATE_HEADERS[format], ...TEMPLATE_ROWS[format]];
}

/** RFC 4180 CSV — quotes only where a cell needs them. */
export function toCsv(rows) {
  return rows
    .map((r) => r.map((v) => {
      const s = String(v ?? "");
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(","))
    .join("\r\n");
}
